import * as Sentry from "@sentry/browser";
import invariant from "tiny-invariant";
import { createReference } from "../lib/firebase";
import { FirebaseStore } from "../lib/FirebaseStore";
import type { GroupedPresenceData } from "./PresenceStore";
import { PresenceStore } from "./PresenceStore";
import type { SequenceData } from "./SequenceStore";

const FIREBASE_KEY = "users";

// TYPES
// -----

export interface UserData {
  /** Used for sorting and consistently coupling them */
  creationDate: number | null;

  /**
   * User name and unique identifier
   *
   * @note Required, and guaranteed present in a `UsersByName` — but only
   * because reads are validated at the app boundary. Raw Firebase data is
   * `UnvalidatedUsersByName`, where it may be missing.
   */
  name: string;

  /** The current interface that the user is seeing */
  currentInterface: string | null;

  /** The url of the user's selfie */
  selfieUrl: string | null;

  /** The urls of the images that are displayed in the interface */
  images: string[];

  /**
   * The ids of the partners that the user has in the current round
   * @note This includes the user itself
   */
  groupUserIds?: string[];

  /** The index of the user that is selected from the group to do something */
  groupSelectedIndex?: number | null;

  /** show a toaster text while this attribute exists */
  alertTextKey?: string | null;

  /** show a toaster while alertTextKey exists */
  alertButtonKey?: string | null;

  /** partner names in sentence-part form */
  partners?: string | null;

  /** mix of traits in sentence-part form */
  amalgamation?: string | null;
  /** traits to choose from: */
  somethingFun?: string | null;
  notInherited?: string | null;
  shouldListenTo?: string | null;

  innerCircle?: boolean | null; // for the game round

  /** optional child names either entered by the groups or random  */
  childName?: string | null;
  grandChildName?: string | null;
  greatGrandChildName?: string | null;
  greatGreatGrandChildName?: string | null;
  otherGreatGreatGrandChildName?: string | null;

  agreedToSelfieStorage?: boolean | null;

  familyTreeParentCount?: number | null;

  gameChoices?: {
    grandparents?: string;
    parents?: string;
    children?: string;
    greatGrandChildren?: string;
  };

  /** User-specific audio sequence (overrides global sequence when present) */
  sequence?: SequenceData | null;
}

/** Users that have passed boundary validation: every record has a `name`. */
export interface UsersByName {
  [name: string]: UserData;
}

/**
 * Users exactly as they arrive from Firebase: unvalidated, so a corrupt or
 * phantom record may lack the `name` that `UserData` declares as required —
 * such records have reached production (ANCESTORS-4B).
 *
 * Read raw user data as this type and validate it into a `UsersByName` once,
 * at the boundary where it enters an app, so that no consumer downstream has
 * to second-guess `name`.
 *
 * See INVESTIGATIONS/round1-orphan-ancestor.md
 */
export interface UnvalidatedUsersByName {
  [name: string]: Omit<UserData, "name"> & { name?: string };
}

export type CreateUserError =
  | "username-taken"
  | "username-invalid-length"
  | "username-invalid-characters";

// ALL USERS
// ---------

interface AllUsersOptions {
  eventPrefix: string;
}

export class AllUsersStore extends FirebaseStore<UsersByName> {
  private _presenceStore: PresenceStore;

  constructor({ eventPrefix }: AllUsersOptions) {
    super(createReference(eventPrefix, FIREBASE_KEY));
    this._presenceStore = new PresenceStore({ eventPrefix });
  }

  /** Applies an update to all users in the database. */
  async updateAllUsers(partialUser: Partial<UserData>) {
    await this.transaction((users) => {
      if (!users) return null;

      for (const name in users) {
        users[name] = {
          ...users[name],
          ...partialUser,
        };
      }

      return users;
    });
  }

  /** Applies an update to all users in the database. */
  async updateOnlySelectedUsers(partialUser: Partial<UserData>, selection: string[]) {
    await this.transaction((users) => {
      if (!users) return null;

      for (const name in users) {
        if (selection.includes(name)) {
          users[name] = {
            ...users[name],
            ...partialUser,
          };
        }
      }

      return users;
    });
  }

  /** Applies an update to the group the user is part of in the database. */
  async updateUserGroup({
    name,
    ...partialUser
  }: Partial<Omit<UserData, "name">> & { name: string }) {
    try {
      return await this.transaction((currentUsers) => {
        if (!currentUsers) return null;

        const user = currentUsers[name];
        invariant(user, `User with name '${name}' not found`);

        const userIds = user.groupUserIds ?? [];

        for (const id of userIds) {
          // A groupUserId can point at a user that no longer exists — a dangling
          // reference, typically left behind after a deletion/replacement. Spreading
          // `...currentUsers[id]` (undefined) here would resurrect a record with no
          // `name`, which later crashes consumers like PresenceCard's sort.
          // Skip and report so the orphan source stays visible.
          // See INVESTIGATIONS/round1-orphan-ancestor.md
          if (!currentUsers[id]) {
            Sentry.captureMessage(`UserStore: dangling groupUserId in updateUserGroup`, {
              level: "error",
              extra: { danglingId: id, groupOwner: name },
            });
            continue;
          }

          currentUsers[id] = {
            ...currentUsers[id],
            ...partialUser,
          };
        }

        return currentUsers;
      });
    } catch (error) {
      console.error(`[UserStore] updateUserGroup failed for '${name}':`, error);
      throw error;
    }
  }

  getNextUserGroupSelectedIndex({
    user,
    presence,
  }: {
    user: UserData;
    presence: GroupedPresenceData;
  }) {
    // Get the currently selected userId
    const groupUserIds = user.groupUserIds ?? [];
    const currentIndex = user.groupSelectedIndex ?? 0;
    const currentUserId = groupUserIds[currentIndex];

    // If no one is online, we can't select anyone else
    const onlineUsers = groupUserIds.filter((userId) =>
      presence.online.some((u) => u.userId === userId),
    );

    if (onlineUsers.length === 0) {
      return null;
    }

    // Pick the next online user
    const onlineIndex = onlineUsers.includes(currentUserId)
      ? onlineUsers.indexOf(currentUserId)
      : 0;
    const newOnlineIndex = (onlineIndex + 1) % onlineUsers.length;
    const newUserId = onlineUsers[newOnlineIndex];
    const newIndex = groupUserIds.indexOf(newUserId);

    invariant(newIndex !== -1, "New user index should be valid");

    return newIndex;
  }

  async incrementUserGroupSelectedIndex(name: string) {
    const presence = await this._presenceStore.getPresence();

    await this.transaction((currentUsers) => {
      if (!currentUsers) return currentUsers;

      const user = currentUsers[name];
      if (!user) return currentUsers;

      const newIndex = this.getNextUserGroupSelectedIndex({ user, presence });
      const groupUserIds = user.groupUserIds ?? [];

      for (const id of groupUserIds) {
        if (currentUsers[id]) {
          currentUsers[id] = {
            ...currentUsers[id],
            groupSelectedIndex: newIndex,
          };
        }
      }

      return currentUsers;
    });
  }

  /**
   * Creates a new user in the database and returns an error if the username is invalid.
   * The username must be between 1 and 20 characters long and cannot contain any of the following characters: . $[ ]#
   */
  async createUser(username: string): Promise<CreateUserError | null> {
    const trimmedUsername = username.trim();

    if (trimmedUsername.length > 20 || trimmedUsername.length === 0) {
      return "username-invalid-length";
    }

    // https://firebase.google.com/docs/database/web/structure-data#how_data_is_structured_its_a_json_tree
    if (trimmedUsername.match(/[.$[\]#/]/)) {
      return "username-invalid-characters";
    }

    const usernameExists = await this.usernameExists(trimmedUsername);

    if (usernameExists) {
      return "username-taken";
    }

    const user: UserData = {
      name: trimmedUsername,
      creationDate: null,
      currentInterface: null,
      selfieUrl: null,
      images: [],
    };

    await this.update({ [trimmedUsername]: user });

    return null;
  }

  /**
   * Checks if a username exists.
   *
   * @note When it finds a username that matches, it will return the found username. Since the
   * search is case insensitive, the returned username might differ from the one provided. Please
   * use the returned username for any future operations.
   */
  async usernameExists(username: string) {
    const users = (await this.get()) ?? {};
    return (
      Object.keys(users).find((name) => name.toLowerCase() === username.trim().toLowerCase()) ??
      null
    );
  }

  async usersWithSelfie() {
    const users = (await this.get()) ?? {};
    const withSelfies: UserData[] = [];

    for (const name of Object.keys(users)) {
      if (users[name].selfieUrl) {
        withSelfies.push(users[name]);
      }
    }

    return withSelfies;
  }

  async getPresence() {
    const users = (await this.get()) ?? {};
    const userIds = Object.keys(users);

    return await this._presenceStore.getPresence(userIds);
  }

  subscribeToPresence(callback: (presence: GroupedPresenceData) => void) {
    let userIds: string[] | null = null;
    let presence: GroupedPresenceData | null = null;

    let unsubscribeFromUserStore = () => {};
    let unsubscribeFromPresenceStore = () => {};

    function notify() {
      if (!userIds || !presence) return;

      callback({
        online: presence.online.filter(({ userId }) => userIds!.includes(userId)),
        offline: presence.offline.filter(({ userId }) => userIds!.includes(userId)),
      });
    }

    unsubscribeFromUserStore = this.subscribe((users) => {
      userIds = Object.keys(users ?? {});
      notify();
    });

    unsubscribeFromPresenceStore = this._presenceStore.subscribeToPresence((data) => {
      presence = data;
      notify();
    });

    return () => {
      unsubscribeFromUserStore();
      unsubscribeFromPresenceStore();
    };
  }
}

// USER
// ----

interface UserOptions {
  eventPrefix: string;
  name: string;
}

export class UserStore extends FirebaseStore<UserData> {
  private _name: string;

  constructor({ eventPrefix, name }: UserOptions) {
    super(createReference(eventPrefix, FIREBASE_KEY, name));
    this._name = name;
  }

  /**
   * Merges `partialUser` into this user, but only if the record still exists.
   *
   * @note Prefer this over `update`. A Firebase `update()` on a path whose node is gone
   * *recreates* that node holding only the fields passed in — so a user deleted from the
   * dashboard gets resurrected without a `name`, which crashes consumers that sort or key on
   * it (ANCESTORS-4B). Creating a user is `AllUsersStore.createUser`'s job alone.
   *
   * @returns whether the user existed and the update was applied.
   * See INVESTIGATIONS/round1-orphan-ancestor.md
   */
  async updateIfExists(partialUser: Partial<Omit<UserData, "name">>) {
    let existed = false;

    await this.transaction((user) => {
      existed = !!user;

      // Writing null to an already-absent path leaves it absent, which is what we want. Do NOT
      // abort with `undefined` here: Firebase runs this callback against its local cache first,
      // and an abort short-circuits before the server is ever consulted, so an unsynced path
      // would refuse a legitimate write. Returning a value keeps the optimistic write, which the
      // server rejects when our view was stale — and then re-runs this callback with real data.
      if (!user) return null;

      return { ...user, ...partialUser };
    });

    if (!existed) {
      Sentry.captureMessage("UserStore: skipped update for a user that no longer exists", {
        level: "warning",
        extra: { name: this._name, fields: Object.keys(partialUser) },
      });
    }

    return existed;
  }
}
